// ── skill detection, anywhere ───────────────────────────────────────────────
// The skill class is a MODIFIER on an ordinary document: same markdown, plus a
// manifest. That means any markdown in the system — a dimension in a project, a
// file on disk, a shared page — becomes a skill the moment its frontmatter says
// `kind: skill`. This module is that recognition, client-safe (no node:fs), so
// the dashboard tree, the share page, and the doc renderer all read the same
// law. The fs-backed loader (./index.ts, server-only) builds on it too.

import { SKILL_KIND, headingId, type SkillManifest } from "./types";

export interface ParsedSkill {
  manifest: SkillManifest;
  /** the markdown body, manifest stripped. */
  body: string;
  /** the document exactly as authored — what travels when the skill is handed on. */
  raw: string;
  minutes: number;
  outline: { id: string; text: string }[];
}

// A deliberately small YAML subset — scalars, `>` folded and `|` literal blocks,
// and inline [a, b] arrays. That is the entire manifest vocabulary; anything
// richer would be a schema the format doesn't have.
export function parseFrontmatter(src: string): { meta: Record<string, string | string[]>; body: string } {
  if (!src.startsWith("---")) return { meta: {}, body: src };
  const end = src.indexOf("\n---", 3);
  if (end < 0) return { meta: {}, body: src };
  const head = src.slice(src.indexOf("\n") + 1, end);
  const body = src.slice(src.indexOf("\n", end + 1) + 1);

  const meta: Record<string, string | string[]> = {};
  const lines = head.split("\n");
  for (let i = 0; i < lines.length; i++) {
    const m = /^([a-z_][a-z0-9_]*):\s*(.*)$/i.exec(lines[i]);
    if (!m) continue;
    const key = m[1];
    let value = m[2].trim();

    if (value === ">" || value === "|" || value === ">-" || value === "|-") {
      const folded = value.startsWith(">");
      const block: string[] = [];
      while (i + 1 < lines.length && /^\s{2,}\S/.test(lines[i + 1])) block.push(lines[++i].trim());
      meta[key] = folded ? block.join(" ") : block.join("\n");
      continue;
    }

    if (value.startsWith("[") && value.endsWith("]")) {
      meta[key] = value
        .slice(1, -1)
        .split(",")
        .map((s) => s.trim().replace(/^["']|["']$/g, ""))
        .filter(Boolean);
      continue;
    }

    value = value.replace(/^["']|["']$/g, "");
    if (value) meta[key] = value;
  }
  return { meta, body };
}

const str = (v: string | string[] | undefined): string | undefined => (typeof v === "string" ? v : undefined);

/** Recognise a skill in any markdown. Returns null for ordinary documents —
 *  the modifier is `kind: skill` plus a description (a document that can't say
 *  what it makes you able to do isn't one). */
export function parseSkillDoc(src: string, fallbackName = "skill"): ParsedSkill | null {
  if (!src || !src.startsWith("---")) return null;
  const { meta, body } = parseFrontmatter(src);
  if (str(meta.kind) !== SKILL_KIND) return null;
  const description = str(meta.description);
  if (!description) return null;

  const name = str(meta.name) ?? fallbackName;
  const outline: { id: string; text: string }[] = [];
  for (const line of body.split("\n")) {
    const h = /^##\s+(.+?)\s*$/.exec(line);
    if (h) outline.push({ id: headingId(h[1]), text: h[1] });
  }

  return {
    manifest: {
      name,
      title: str(meta.title) ?? name.replace(/-/g, " "),
      kind: SKILL_KIND,
      description,
      when_to_load: str(meta.when_to_load),
      version: str(meta.version),
      updated: str(meta.updated),
      authored_by: str(meta.authored_by),
      author_url: str(meta.author_url),
      source_url: str(meta.source_url),
      source_raw: str(meta.source_raw),
      license: str(meta.license),
      brought_by: str(meta.brought_by),
      tags: Array.isArray(meta.tags) ? meta.tags : [],
      category: str(meta.category),
    },
    body,
    raw: src,
    minutes: Math.max(1, Math.round(body.split(/\s+/).length / 220)),
    outline,
  };
}
